/**
 * etaModel — PURE: elapsed time, progress and the build-phase pace estimate for the task panel (leg `gui-eta`).
 *
 * No React, no I/O, `now` is a parameter. Inputs are the R3 `TaskSnapshot` only; nothing is invented:
 *  - ELAPSED: earliest `transitions[].at` (fallback `created_at`) to now; for a terminal task, to its last transition.
 *  - PROGRESS: "N of M items" from `build.counts` (server-authoritative, recomputed from CURRENT statuses on every
 *    snapshot, so an item reopened from settled back to pending simply lowers N), and "level k of L" from the items'
 *    `level` (k = lowest level that still has unresolved work).
 *  - SETTLED = the server's resolved set, read from `boardModel.RESOLVED_BUILD_STATUSES` (one owner, rule 21b).
 *  - PACE: remaining / (settled per minute since the build started). Build phase only: the org check that follows is
 *    NOT included and the label says so. Never a number below the thresholds, never a confident number while paused.
 */
import type { TaskSnapshot, TransitionView, WorkItemView } from "@/services/multi-agent/laneV2/types";
import { isResolvedBuildStatus } from "./boardModel";
import { buildCounts, isTerminalState } from "./views";

/** Fewer settled items than this and the rate is noise (one fast item would promise a minute for a 60-item build). */
export const ETA_MIN_SETTLED = 3;
/** Less build time than this and the rate is dominated by start-up (first lease, first generation). */
export const ETA_MIN_ELAPSED_MS = 2 * 60_000;
/** An active build in which nothing has settled for this long is "no recent progress", not a countdown. A single
 *  item can legitimately take several minutes (generation plus repair attempts), so this is deliberately generous. */
export const ETA_STALL_MS = 10 * 60_000;
/** Beyond this the pace figure carries no information; say so instead of printing a huge number. */
export const ETA_MAX_MINUTES = 180;

/** States before a build has started. */
const PRE_BUILD_STATES: ReadonlySet<string> = new Set(["exploring", "plan_proposed", "contracting"]);

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/**
 * Task start: R3 `created_at`; the earliest transition only as a fallback (the server keeps at most 500 transition
 * rows, so on a long task the earliest ROW can be later than the start). Clamped >= 0 by the caller: the client
 * clock can run behind the server's, which would otherwise show a negative elapsed time.
 */
export function taskStartMs(snapshot: Pick<TaskSnapshot, "transitions" | "created_at">): number | null {
  const created = ms(snapshot.created_at);
  if (created !== null) return created;
  const ats = snapshot.transitions.map((t) => ms(t.at)).filter((t): t is number => t !== null);
  return ats.length > 0 ? Math.min(...ats) : null;
}

/** Waiting on a person: the clock for the build does not run, and no estimate is honest. */
const WAITING_ON_PERSON: ReadonlySet<string> = new Set(["needs_input", "needs_budget", "amendment_proposed"]);
/** States between `building` segments. Re-entry from assembling/checking/changeset_proposed always comes with a NEW build (a
 *  new build_materialized), so those entries only matter on the no-materialization fallback path. */
const WAITING_STATES: ReadonlySet<string> = new Set([...WAITING_ON_PERSON, "assembling", "checking", "changeset_proposed"]);

/**
 * Index of the transition that starts the latest build's clock: the latest `build_materialized` (the server records it as
 * a building->building self-loop, so it is NOT an entry), else the latest contracting->building entry. null = no build yet.
 */
export function buildStartIndex(transitions: readonly TransitionView[]): number | null {
  let entry: number | null = null;
  for (let i = transitions.length - 1; i >= 0; i--) {
    if (transitions[i].event === "build_materialized" && transitions[i].to === "building") return i;
    // No materialization row (older tasks, or trimmed by the 500-row cap): the build began at contract_ready
    // (contracting->building). A re-entry from a waiting state must NOT restart the clock, so it is never the fallback.
    if (entry === null && transitions[i].to === "building" && transitions[i].from === "contracting") entry = i;
  }
  return entry;
}

/**
 * ACTIVE build time: the sum of the `building` segments from the latest materialization on, up to `capMs`. Time spent
 * waiting (needs_input, needs_budget, amendment_proposed, assembling, checking, changeset_proposed) is excluded, so a
 * build resumed after a long park does not restart its pace at zero nor count the park as work. Self-loops
 * (building->building) are contiguous building time and simply extend the segment. Stops after the first exit from
 * `building` to a state that is not a waiting state (the build phase is over). `endedAtMs` is that exit time, if seen.
 */
export function activeBuildTime(transitions: readonly TransitionView[], startIdx: number, capMs: number): { activeMs: number; endedAtMs: number | null } {
  let activeMs = 0;
  let endedAtMs: number | null = null;
  for (let i = startIdx; i < transitions.length; i++) {
    const t = transitions[i];
    if (t.to !== "building") continue;
    const segStart = ms(t.at);
    if (segStart === null) continue;
    const next = i + 1 < transitions.length ? transitions[i + 1] : null;
    const nextAt = next ? ms(next.at) : null;
    const segEnd = Math.min(capMs, nextAt ?? capMs);
    activeMs += Math.max(0, segEnd - segStart);
    if (next && next.from === "building" && next.to !== "building" && !WAITING_STATES.has(next.to)) {
      endedAtMs = nextAt;
      break;
    }
  }
  return { activeMs, endedAtMs };
}

export function formatDuration(totalMs: number): string {
  const mins = Math.floor(Math.max(0, totalMs) / 60_000);
  if (mins < 1) return "under a minute";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export interface Progress {
  /** Resolved (built, validated, failed, blocked) - what the pace counts. Dropped items are out of BOTH numbers: they
   *  are not build work (they are dropped in bulk when the build is materialized) and would skew the rate. */
  settled: number;
  /** Items that finished building (built or validated) - the honest "N" shown to the reader (rule 23). */
  finished: number;
  /** failed + blocked. */
  problem: number;
  /** Items excluding dropped. */
  total: number;
  /** null when no item carries a level. */
  level: { k: number; of: number } | null;
}

export function progressOf(build: NonNullable<TaskSnapshot["build"]>): Progress {
  const counts = buildCounts(build);
  let settled = 0;
  let total = 0;
  let finished = 0;
  let problem = 0;
  for (const [status, n] of Object.entries(counts)) {
    if (status === "dropped") continue;
    total += n;
    if (isResolvedBuildStatus(status)) settled += n;
    if (status === "built" || status === "validated") finished += n;
    if (status === "failed" || status === "blocked") problem += n;
  }
  const live = build.items.filter((i: WorkItemView) => i.status !== "dropped");
  const levels = [...new Set(live.map((i) => i.level).filter((l): l is number => typeof l === "number"))].sort((a, b) => a - b);
  let level: Progress["level"] = null;
  if (levels.length > 0) {
    const open = levels.findIndex((l) => live.some((i) => i.level === l && !isResolvedBuildStatus(i.status)));
    level = { k: (open === -1 ? levels.length - 1 : open) + 1, of: levels.length };
  }
  return { settled, finished, problem, total, level };
}

export type EtaSlot =
  | { kind: "none" }
  | { kind: "placeholder" }
  | { kind: "gathering" }
  | { kind: "estimate"; minutesLeft: number; capped: boolean }
  | { kind: "finishing" }
  | { kind: "paused"; why: "parked" | "awaiting_human" }
  | { kind: "stalled"; sinceMs: number }
  | { kind: "repair"; toRebuild: number }
  | { kind: "unknown_start" }
  | { kind: "done"; durationMs: number | null };

export interface EtaView {
  elapsedMs: number | null;
  progress: Progress | null;
  slot: EtaSlot;
}

export function deriveEta(snapshot: TaskSnapshot, nowMs: number): EtaView {
  const start = taskStartMs(snapshot);
  const terminal = isTerminalState(snapshot.state);
  const lastAt = snapshot.transitions.map((t) => ms(t.at)).reduce<number | null>((a, t) => (t !== null && (a === null || t > a) ? t : a), null);
  const end = terminal && lastAt !== null ? lastAt : nowMs;
  const elapsedMs = start === null ? null : Math.max(0, end - start);

  const build = snapshot.build;
  if (!build) {
    return { elapsedMs, progress: null, slot: terminal ? { kind: "none" } : { kind: "placeholder" } };
  }
  const progress = progressOf(build);
  const startIdx = buildStartIndex(snapshot.transitions);
  const timing = startIdx === null ? null : activeBuildTime(snapshot.transitions, startIdx, nowMs);

  // The build phase is over (done/failed/cancelled/superseded, or the task has moved past `building`): active duration.
  const phaseOver = build.status !== "active" && build.status !== "parked";
  if (phaseOver || (timing?.endedAtMs != null && snapshot.state !== "building" && !WAITING_ON_PERSON.has(snapshot.state))) {
    return { elapsedMs, progress, slot: { kind: "done", durationMs: timing && timing.activeMs > 0 ? timing.activeMs : null } };
  }
  // Waiting on a person outranks a parked build: the useful message is "it is your move".
  if (WAITING_ON_PERSON.has(snapshot.state)) return { elapsedMs, progress, slot: { kind: "paused", why: "awaiting_human" } };
  if (build.status === "parked") return { elapsedMs, progress, slot: { kind: "paused", why: "parked" } };
  const remaining = progress.total - progress.settled;
  // A repair build (it has predecessors) re-runs copied items: the pace since ITS start says nothing about the work left.
  if (build.predecessors && build.predecessors.length > 0) return { elapsedMs, progress, slot: { kind: "repair", toRebuild: Math.max(0, remaining) } };
  if (PRE_BUILD_STATES.has(snapshot.state)) return { elapsedMs, progress, slot: { kind: "placeholder" } };
  // The build HAS started but its start row is not in the (<=500) transitions: say so, never "once the build starts".
  if (startIdx === null || timing === null) return { elapsedMs, progress, slot: { kind: "unknown_start" } };
  if (remaining <= 0) return { elapsedMs, progress, slot: { kind: "finishing" } };

  // Stall rule. `updated_at` is NOT the settle time: label refreshes, level and depends_on rewrites also bump it (the true
  // field, work_attempts.ended_at, is not on the wire; a future R3 `last_progress_at` should replace this). It is used for
  // the stall rule only, where a late bump errs toward "still moving". The clock never starts before the latest resume.
  const resumeMs = ms(snapshot.transitions[startIdx].at) ?? nowMs;
  let resumedAt = resumeMs;
  for (let i = snapshot.transitions.length - 1; i >= startIdx; i--) {
    const t = snapshot.transitions[i];
    if (t.to === "building" && t.from !== "building") {
      resumedAt = Math.max(resumeMs, ms(t.at) ?? resumeMs);
      break;
    }
  }
  const lastSettled = build.items
    .filter((i) => isResolvedBuildStatus(i.status) && i.status !== "dropped")
    .map((i) => ms(i.updated_at))
    .reduce<number>((a, t) => (t !== null && t > a ? t : a), resumedAt);
  if (nowMs - lastSettled >= ETA_STALL_MS) return { elapsedMs, progress, slot: { kind: "stalled", sinceMs: nowMs - lastSettled } };

  if (progress.settled < ETA_MIN_SETTLED || timing.activeMs < ETA_MIN_ELAPSED_MS) return { elapsedMs, progress, slot: { kind: "gathering" } };

  const perMinute = progress.settled / (timing.activeMs / 60_000);
  const minutes = remaining / perMinute;
  return { elapsedMs, progress, slot: { kind: "estimate", minutesLeft: Math.min(ETA_MAX_MINUTES, Math.max(1, Math.round(minutes))), capped: minutes > ETA_MAX_MINUTES } };
}
